import { Button, CircularProgress, Paper, Stack } from '@mui/material'
import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import GetCookie from './common/getCookie'

import CookiesDialog from '../CookiesDialog/CookiesDialog'
import { SocialType } from '../SocialLogin/SocialLogin'
import { useNewContent } from '../hooks/useNewContent'
import { useResponsiveSizings } from '../hooks/useResponsiveSizings'
import SettingsDialog from '../settings/SettingsDialog'
import { ServerErrors } from '../utils/serverErrors'
import { PiecesTheme } from './common/PiecesProvider'
import { clearQueryParams } from './common/queryParams'
import BurgerNavigation from './components/BurgerNavigation'
import GlobalLink from './components/GlobalLink'
import UserMenu from './components/UserMenu'
import ConfirmEmailForm from './forms/ConfirmEmailForm'
import DiveInForm from './forms/DiveInForm'
import ErrorForm from './forms/ErrorForm'
import ForgotPasswordForm from './forms/ForgotPasswordForm'
import JoinUsForm from './forms/JoinUsForm'
import LogInForm from './forms/LogInForm'
import ResetPasswordForm from './forms/ResetPasswordForm'
import SignUpForm from './forms/SignUpForm'
import SignUpSocialForm from './forms/SignUpSocialForm'
import {
    ensureLogin,
    getUserData,
    guestRegistration,
    loginSocial,
    logout,
    resendConfirmationEmail,
    signup,
    upgradeGuest,
    upgradeGuestSocial,
} from './functions/ams-functions'
import { AnalyticsEventMap, analyticsManager } from './services/analytics/AnalyticsManager'

let newPasswordTokens: string[] = []
let lastToken = ''

export type UserRatingData = {
    best: number
    best_date: string
    draw: number
    game_type: string
    is_provisional: boolean
    loss: number
    rating: number
    total_games: number
    user_id: string
    win: number
}

export type Settings = {
    autoAccept: boolean
    boardStyle: string
    coordinates: boolean
    dragAndDrop: boolean
    pieceStyle: PiecesTheme
    preMove: boolean
    soundCheckOn: boolean
    soundDrawOn: boolean
    soundMateOn: boolean
    soundMoveOn: boolean
    soundOn: boolean
    soundSmateOn: boolean
    autoQueen: boolean
    darkMode: boolean
    soundStyle: string
    briefToggle: boolean
    friendlyMode: boolean
    showLegalMoves: boolean
    engineToggle: boolean
    evaluationBar: boolean
    highlightTopMoves: boolean
    multipleLines: number
}

export enum Rules {
    FULL_ACCESS = 'full_access',
    GAME_HISTORY = 'game_history',
    GAME_SETTINGS = 'game_settings',
    LIVE_GAMES = 'live_games',
    PROFILE = 'profile',
    NEWS = 'news',
    VIDEOS = 'videos',
    USER_MEMBERSHIPS = 'user_memberships',
    PLAY_BOTS = 'play_bots',
    FOLLOW = 'follow',
}

export enum GroupNames {
    GUEST = 'guest',
    MEMBER = 'member',
    SUBSCRIBED = 'subscribed',
}

export type UserGroup = {
    name: GroupNames
    id: number
    rules: [
        {
            // TODO: add rules as they are defined
            name: Rules
        },
    ]
}

export type UserData = {
    id: string
    user_name: string
    first_name?: string
    last_name?: string
    title: string | null
    avatar_url: string | null
    email: string
    birthday?: string
    phone_number?: string
    country?: string
    email_confirmed: boolean
    created_at: any
    over_12_years_old: boolean
    subscribed_to_mailing_list: boolean
    subscription_state: number
    subscription_days_left: number
    free_login: boolean
    ratings: {
        blitz: UserRatingData
        bullet: UserRatingData
        classic: UserRatingData
        rapid: UserRatingData
    }
    ftue: {
        mode: 'guest' | 'user'
        newGame: boolean
        players: boolean
        computer: boolean
        analysis: boolean
        puzzle: boolean
        /** @deprecated not in use anymore */
        welcomeGuest: boolean
        /** @deprecated not in use anymore */
        welcomeUser: boolean
        puzzleQuest: boolean
    }
    newContent: {
        home: boolean
        play: boolean
        watch: boolean
        analysis: boolean
        puzzleQuest: boolean
        puzzles: boolean
        dailyPuzzle: boolean
        leaderboard: boolean
        videos: boolean
        news: boolean
        shop: boolean
        memberships: boolean
    }
    game_settings: Settings
    groups: Array<UserGroup>
    reward: {
        tier: number
        season: string
    }
    botd_reward_url?: string
}

export type FTUE = keyof Pick<UserData, 'ftue'>['ftue']

export type LoggedInUserData = {
    state: LoginState
    userData: UserData | null
    token: string
}

export enum AreaType {
    WEBSITE = 'website',
    PLAY_AREA = 'play_area',
}

export interface GlobalHeaderProps {
    apiURL: string
    onSettingsChange: (data: Settings) => void
    onLoginStateChange: (data: LoggedInUserData) => void
    extraButtons?: Array<JSX.Element>
    playAreaURL: string
    websiteURL: string
    forumsURL: string
    learningURL: string
    shopURL: string
    env: string
    gaGameKey?: string
    gaSecretKey?: string
    area: AreaType
    googleClientId?: string
    facebookAppId?: string
    facebookApiVersion?: string
    appleClientId?: string
    appleRedirectURL?: string
    initialUserData?: UserData | null
    Link: any
}

export enum OverlayType {
    NONE,
    SIGNUP,
    SIGNUP_SOCIAL,
    SIGNIN,
    FORGOT_PASSWORD,
    RESET_PASSWORD,
    CONFIRM_EMAIL,
    JOIN_US,
    DIVE_IN,
    ERROR,
    SETTINGS,
    EMAIL_NOT_CONFIRMED,
}

export enum LoginState {
    NOT_LOGGED_IN = 'not_logged_in',
    LOGGED_IN = 'logged_in',
    INITIALIZING = 'initializing',
}
export enum ErrorAction {
    GUEST_REGISTRATION = 'guestRegistration',
    SOCIAL_LOGIN = 'socialLogin',
}

export const resendEmailTimeout = 60 // 1 minute

function GlobalHeader(props: GlobalHeaderProps, ref: any) {
    const {
        onSettingsChange,
        onLoginStateChange,
        apiURL,
        extraButtons,
        websiteURL,
        playAreaURL,
        learningURL,
        forumsURL,
        shopURL,
        area,
        env,
        googleClientId,
        facebookAppId,
        facebookApiVersion,
        appleClientId,
        appleRedirectURL,
        initialUserData,
        Link,
    } = props

    const [loginState, setLoginState] = useState(!!initialUserData ? LoginState.LOGGED_IN : LoginState.INITIALIZING)
    const [overlay, setOverlay] = useState(OverlayType.NONE)
    const [socialData, setSocialData] = useState<any>(null)
    const [resentEmail, setResentEmail] = useState<'' | 'success' | 'error'>('')
    const [resentEmailMessage, setResentEmailMessage] = useState('')
    const [resentMessageCountdown, setResentMessageCountdown] = useState(resendEmailTimeout)
    const [registerEmail, setRegisterEmail] = useState<string>('')
    const [errorAction, setErrorAction] = useState<ErrorAction | null>(null)
    const ensuredRef = useRef<Boolean>(false)
    const { resolution } = useResponsiveSizings()

    const [userData, setUserData] = useState<UserData | null>(initialUserData || null)

    const newContentProvider = useNewContent(userData, setUserData, area)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const search = window.location.search
            const params = new URLSearchParams(search)

            const overlay = params.get('overlay')?.toLocaleUpperCase() as keyof typeof OverlayType
            if (overlay) {
                const allowedOverlays = ['SIGNUP', 'SIGNIN', 'FORGOT_PASSWORD', 'SETTINGS']
                allowedOverlays.includes(overlay) && setOverlay(OverlayType[overlay])
                clearQueryParams(['overlay'])
            }

            newPasswordTokens = params.getAll('token')
            const isResetPasswordFlow = params.has('resetPassword')
            lastToken = newPasswordTokens[newPasswordTokens.length - 1]
            if (isResetPasswordFlow && lastToken) {
                setOverlay(OverlayType.RESET_PASSWORD)
            }
        }
    }, [])

    useEffect(() => {
        if (initialUserData) {
            // quick fix for bi-directional data flow, in future should be adressed in a better way
            setUserData({ ...initialUserData, newContent: userData?.newContent || initialUserData.newContent })
        }
    }, [initialUserData])

    useEffect(() => {
        if (loginState === LoginState.LOGGED_IN) {
            const fullUserData = getUserData()
            if (!!fullUserData) {
                setUserData(fullUserData.user)
            }
        }
    }, [loginState])

    const [isGuest, setIsGuest] = useState(
        initialUserData?.groups.some((group: UserGroup) => group.name === GroupNames.GUEST) || false,
    )

    useEffect(() => {
        if (window.location) {
            const urlParams = new URLSearchParams(decodeURIComponent(window.location.search))
            const emailConfirmed = urlParams.get('emailConfirmed')
            if (emailConfirmed === 'true') {
                analyticsManager.dispatchEvent('emailActivated', {})
                clearQueryParams(['emailConfirmed'])
            }
            if (emailConfirmed === 'false') {
                setOverlay(OverlayType.EMAIL_NOT_CONFIRMED)
            }
        }
    }, [])

    const onAutoOpenSignInForm = () => {
        if (overlay === OverlayType.NONE) {
            setOverlay(OverlayType.SIGNIN)
        }
    }

    useImperativeHandle(ref, () => ({
        openSignUpForm: () => setOverlay(OverlayType.SIGNUP),
        openSignUpSocialForm: () => setOverlay(OverlayType.SIGNUP_SOCIAL),
        openSignInForm: () => setOverlay(OverlayType.SIGNIN),
        openJoinUsForm: () => setOverlay(OverlayType.JOIN_US),
        openDiveInForm: () => setOverlay(OverlayType.DIVE_IN),
        openResetPasswordForm: () => setOverlay(OverlayType.RESET_PASSWORD),
        openProfileSettings: () => setOverlay(OverlayType.SETTINGS),
        openError: (action: ErrorAction) => onErrorHandler(action),
        onSignUpAsGuest: () => onGuestRegistration(),
        onSignUpSocial: (socialResponse: any) => onSignUpSocialClick(socialResponse),
        onSuccessLogin: () => onLoginSuccessful(),
        onSettingsUpdate: (settings: Settings) => localSettingsUpdate(settings),
        onLogout: () => logoutHandler(),
        onUpdateUserData: (userData: UserData) => setUserData(userData),
        autoOpenSignInForm: () => onAutoOpenSignInForm(),
    }))

    const localSettingsUpdate = (settings: Settings) => {
        if (userData) {
            setUserData({ ...userData, game_settings: settings })
        }
    }

    const onErrorHandler = (action: ErrorAction) => {
        setErrorAction(action)
        setOverlay(OverlayType.ERROR)
    }

    const ensureLoginCallback = useCallback(async () => {
        if (ensuredRef.current === true) return
        ensuredRef.current = true
        const userData = await ensureLogin()

        if (userData && userData.user_name) {
            setLoginState(LoginState.LOGGED_IN)
            setIsGuest(userData.groups.some((group: UserGroup) => group.name === GroupNames.GUEST))
            setUserData(userData)
            const token = GetCookie('jwt')

            onLoginStateChange({
                state: LoginState.LOGGED_IN,
                userData: userData,
                token: token,
            })
        } else {
            setLoginState(LoginState.NOT_LOGGED_IN)
            onLoginStateChange({
                state: LoginState.NOT_LOGGED_IN,
                userData: null,
                token: '',
            })
        }
    }, [])

    useEffect(() => {
        if (!initialUserData) {
            ensureLoginCallback()
        }
    }, [])

    const onResend = async (event?: any, email?: string) => {
        const result = await resendConfirmationEmail(email || registerEmail)

        if (!result.error) {
            setResentEmailMessage('Email resent successfully.')
            setResentEmail('success')
            setResentMessageCountdown(resendEmailTimeout)
        } else {
            if (result.error.code === ServerErrors.SEND_EMAIL_LIMIT_REACHED) {
                setResentEmailMessage('Too many requests, please try again later.') // send email limitation reached
                setResentEmail('error')
                setResentMessageCountdown(result.error.details.timeLeft)
            } else {
                setResentEmailMessage('Unknown Error, please try again later.')
                setResentEmail('error')
            }
        }
    }

    const logoutHandler = async () => {
        try {
            await logout()
            setOverlay(OverlayType.NONE)
            setLoginState(LoginState.NOT_LOGGED_IN)
            onLoginStateChange({
                state: LoginState.NOT_LOGGED_IN,
                userData: null,
                token: '',
            })
            setUserData(null)
            analyticsManager.dispatchEvent('logout', {})
        } catch (e) {
            console.log('Error when logging out', e)
        }
    }

    const onLoginSuccessful = async () => {
        setOverlay(OverlayType.NONE)
        setLoginState(LoginState.LOGGED_IN)
        const token = GetCookie('jwt')
        const userData = getUserData()
        setSocialData(null)
        setUserData(userData.user)
        setIsGuest(userData.user.groups.some((group: UserGroup) => group.name === GroupNames.GUEST))
        if (userData && userData.user)
            onLoginStateChange({
                state: LoginState.LOGGED_IN,
                userData: userData.user,
                token: token,
            })
    }

    const onGuestRegistration = async () => {
        try {
            const result = await guestRegistration()
            if (!result.error) {
                onLoginSuccessful()
                analyticsManager.dispatchEvent('completeSignup', {
                    authType: 'guest',
                    country: 'NA',
                    userId: result.user.id,
                    userName: result.user.user_name,
                })
            } else {
                onErrorHandler(ErrorAction.GUEST_REGISTRATION)
            }
        } catch (e) {
            console.log('Error when registering guest', e)
        }
    }

    const onSubmit = async (
        email: string,
        password: string,
        username: string,
        country: string,
        over12: boolean,
        emailSubscribe: boolean,
    ) => {
        try {
            let data: any = null
            if (isGuest) {
                data = await upgradeGuest(email, password, username, country, over12, emailSubscribe)
                setLoginState(LoginState.NOT_LOGGED_IN)
                onLoginStateChange({
                    state: LoginState.NOT_LOGGED_IN,
                    userData: null,
                    token: '',
                })
            } else {
                data = await signup(email, password, username, country, over12, emailSubscribe)
            }
            setRegisterEmail(email)

            if (!data?.error) {
                analyticsManager.dispatchEvent('completeSignup', {
                    country,
                    authType: 'email/user_name',
                    userId: data.user.id,
                    userName: data.user.user_name,
                })
            }

            setOverlay(OverlayType.CONFIRM_EMAIL)
        } catch (e) {
            console.log('Error while submitting', e)
        }
    }

    const onSocialSubmit = async (username: string, country: string, over12: boolean, emailSubscribe: boolean) => {
        try {
            let socialToken: string = ''
            let accessToken: string = ''
            switch (socialData.socialType) {
                case SocialType.APPLE:
                    socialToken = socialData.authorization.code
                    accessToken = socialData.socialToken
                    break
                case SocialType.FACEBOOK:
                    socialToken = socialData.authResponse.accessToken
                    accessToken = socialData.authResponse.signedRequest
                    break
                case SocialType.GOOGLE:
                    socialToken = socialData.credential
                    accessToken = socialData.clientId
                    break
                default:
                    break
            }

            let data: any = null
            if (isGuest) {
                data = await upgradeGuestSocial(
                    socialToken,
                    accessToken,
                    socialData.socialType,
                    username,
                    country,
                    over12,
                    emailSubscribe,
                )
            } else {
                data = await loginSocial(
                    socialToken,
                    accessToken,
                    socialData.socialType,
                    username,
                    country,
                    over12,
                    emailSubscribe,
                )
            }

            if (data?.error) {
                onErrorHandler(ErrorAction.SOCIAL_LOGIN)
            } else {
                onLoginSuccessful()
                analyticsManager.dispatchEvent('completeSignup', {
                    country,
                    authType:
                        socialData.socialType === SocialType.APPLE
                            ? 'apple'
                            : socialData.socialType === SocialType.FACEBOOK
                            ? 'facebook'
                            : 'google',
                    userId: data.user.id,
                    userName: data.user.user_name,
                })
            }
        } catch (e) {
            console.log('Error while submitting', e)
        }
    }

    const onSignUpClick = () => {
        setOverlay(OverlayType.SIGNUP)
    }

    const onSignUpSocialClick = (socialResponse: any) => {
        setSocialData(socialResponse)
        setOverlay(OverlayType.SIGNUP_SOCIAL)
    }

    const onErrorTryAgain = () => {
        switch (errorAction) {
            case ErrorAction.GUEST_REGISTRATION:
                onErrorClose()
                break
            case ErrorAction.SOCIAL_LOGIN:
                setOverlay(OverlayType.SIGNUP_SOCIAL)
                break
            default:
                break
        }
        setErrorAction(null)
    }

    const onErrorGoToSignIn = () => {
        setSocialData(null)
        setErrorAction(null)
        setOverlay(OverlayType.SIGNIN)
    }

    const onErrorClose = () => {
        setSocialData(null)
        setErrorAction(null)
        onAuthClose()
    }

    const onAuthClose = (analyticsEvent: keyof AnalyticsEventMap = 'loginDismissed') => {
        setOverlay(OverlayType.NONE)
        if (
            loginState !== LoginState.LOGGED_IN &&
            (area === AreaType.PLAY_AREA || analyticsEvent === 'guestContinued')
        ) {
            analyticsManager.dispatchEvent(analyticsEvent, {})
            onGuestRegistration()
        }
    }

    return (
        <Paper
            sx={{
                px: resolution.isMobile ? '15px' : '40px',
                py: '15px',
                backgroundColor: 'background.default',
                position: 'relative',
                zIndex: 1300,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Fragment>
                <Paper
                    sx={{
                        fontFamily: 'Fira Sans',
                        textTransform: 'uppercase',
                        color: 'text.primary',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" gap={2.5} alignItems="center">
                        <BurgerNavigation
                            user={userData}
                            websiteURL={websiteURL}
                            playAreaURL={playAreaURL}
                            learningURL={learningURL}
                            forumsURL={forumsURL}
                            shopURL={shopURL}
                            area={area}
                            Link={Link}
                        />
                        <GlobalLink
                            Link={Link}
                            href={area === AreaType.PLAY_AREA ? websiteURL : '/'}
                            area={area}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <img src={'/assets/images/logo.svg'} alt="logo" height="50px" width="50px" />
                        </GlobalLink>
                    </Stack>
                    <Stack direction="row" gap={2}>
                        <Stack direction="row" alignItems="center" gap={2}>
                            {extraButtons &&
                                extraButtons.map((button, index) => <Fragment key={index}>{button}</Fragment>)}
                        </Stack>

                        {userData && loginState === LoginState.LOGGED_IN && (
                            <UserMenu
                                onLogout={logoutHandler}
                                userName={userData.user_name}
                                avatarUrl={userData?.avatar_url || ''}
                                userData={userData}
                                playAreaURL={playAreaURL}
                                isGuest={isGuest}
                                area={area}
                                Link={Link}
                                onLogIn={() => {
                                    setOverlay(OverlayType.SIGNIN)
                                    analyticsManager.dispatchEvent('openLogin', {
                                        origin: 'guestProfileDropdown',
                                    })
                                }}
                                onSignUp={() => {
                                    setOverlay(OverlayType.SIGNUP)
                                    analyticsManager.dispatchEvent('openSignup', {
                                        origin: 'guestProfileDropdown',
                                    })
                                }}
                                onSettings={() => {
                                    setOverlay(OverlayType.SETTINGS)
                                }}
                            />
                        )}
                        {loginState === LoginState.NOT_LOGGED_IN && (
                            <Stack direction="row" gap={2}>
                                <Button
                                    sx={{ width: resolution.isMobile ? 'auto' : '200px' }}
                                    onClick={() => {
                                        setOverlay(OverlayType.SIGNIN)
                                        analyticsManager.dispatchEvent('openLogin', {
                                            origin: 'header',
                                        })
                                    }}
                                >
                                    Log in
                                </Button>
                                <Button
                                    sx={{ width: resolution.isMobile ? 'auto' : '200px' }}
                                    onClick={() => {
                                        onSignUpClick()
                                        analyticsManager.dispatchEvent('openSignup', {
                                            origin: 'header',
                                        })
                                    }}
                                >
                                    Sign up
                                </Button>
                            </Stack>
                        )}
                        {loginState === LoginState.INITIALIZING && <CircularProgress />}
                    </Stack>
                </Paper>
                {(!userData || isGuest) && overlay === OverlayType.SIGNIN && (
                    <LogInForm
                        onClose={onAuthClose}
                        onSwichToSignup={() => {
                            setOverlay(OverlayType.SIGNUP)
                            analyticsManager.dispatchEvent('openSignup', {
                                origin: 'loginDialog',
                            })
                        }}
                        onSwitchToForgotPassword={() => setOverlay(OverlayType.FORGOT_PASSWORD)}
                        onSwitchToEmailConfirmation={(email: string) => {
                            setOverlay(OverlayType.CONFIRM_EMAIL)
                            setRegisterEmail(email)
                            onResend(undefined, email)
                        }}
                        onSignUpSocial={onSignUpSocialClick}
                        onLoginSuccessful={onLoginSuccessful}
                        isGuest={isGuest}
                        googleClientId={googleClientId}
                        facebookAppId={facebookAppId}
                        facebookApiVersion={facebookApiVersion}
                        appleClientId={appleClientId}
                        appleRedirectURL={appleRedirectURL}
                    />
                )}

                {(!userData || isGuest) && overlay === OverlayType.SIGNUP && (
                    <SignUpForm
                        onClose={onAuthClose}
                        onSignin={() => {
                            setOverlay(OverlayType.SIGNIN)
                            analyticsManager.dispatchEvent('openLogin', {
                                origin: 'signupDialog',
                            })
                        }}
                        onSignUpSocial={onSignUpSocialClick}
                        onLoginSocialSuccessful={onLoginSuccessful}
                        websiteURL={websiteURL}
                        onSubmit={onSubmit}
                        guestUsername={isGuest ? userData?.user_name : undefined}
                        googleClientId={googleClientId}
                        facebookAppId={facebookAppId}
                        facebookApiVersion={facebookApiVersion}
                        appleClientId={appleClientId}
                        appleRedirectURL={appleRedirectURL}
                        env={env}
                    />
                )}

                {overlay === OverlayType.SIGNUP_SOCIAL && (
                    <SignUpSocialForm onClose={onAuthClose} websiteURL={websiteURL} onSubmit={onSocialSubmit} />
                )}

                {overlay === OverlayType.CONFIRM_EMAIL && (
                    <ConfirmEmailForm
                        onClose={() => {
                            clearQueryParams(['emailConfirmed', 'email'])
                            onAuthClose()
                        }}
                        onResend={onResend}
                        resendMessage={resentEmailMessage}
                        resent={resentEmail}
                        failed={false}
                        countDown={resentMessageCountdown}
                        setCountDown={setResentMessageCountdown}
                    />
                )}

                {overlay === OverlayType.EMAIL_NOT_CONFIRMED && (
                    <ConfirmEmailForm
                        onClose={() => {
                            clearQueryParams(['emailConfirmed', 'email'])
                            onAuthClose()
                        }}
                        onResend={onResend}
                        resendMessage={resentEmailMessage}
                        resent={resentEmail}
                        failed={true}
                        countDown={resentMessageCountdown}
                        setCountDown={setResentMessageCountdown}
                    />
                )}

                {overlay === OverlayType.FORGOT_PASSWORD && <ForgotPasswordForm onClose={onAuthClose} env={env} />}

                {overlay === OverlayType.RESET_PASSWORD && (
                    <ResetPasswordForm
                        onClose={() => {
                            clearQueryParams(['resetPassword', 'token'])
                            setOverlay(OverlayType.SIGNIN)
                        }}
                        newPasswordToken={lastToken}
                    />
                )}

                {overlay === OverlayType.JOIN_US && (
                    <JoinUsForm
                        onClose={() => setOverlay(OverlayType.NONE)}
                        onSignUp={() => setOverlay(OverlayType.SIGNUP)}
                        onSignIn={() => setOverlay(OverlayType.SIGNIN)}
                    />
                )}

                {overlay === OverlayType.DIVE_IN && (
                    <DiveInForm
                        onClose={() => setOverlay(OverlayType.NONE)}
                        onSignUp={() => setOverlay(OverlayType.SIGNUP)}
                        onSignIn={() => setOverlay(OverlayType.SIGNIN)}
                    />
                )}

                {overlay === OverlayType.ERROR && (
                    <ErrorForm onClose={onErrorClose} onSignIn={onErrorGoToSignIn} onTryAgain={onErrorTryAgain} />
                )}

                {userData && overlay === OverlayType.SETTINGS && (
                    <SettingsDialog
                        settings={userData.game_settings}
                        groups={userData.groups}
                        openJoinUsForm={() => setOverlay(OverlayType.JOIN_US)}
                        setSettings={(settings) => {
                            localSettingsUpdate(settings)
                            onSettingsChange(settings)
                        }}
                        onClose={() => setOverlay(OverlayType.NONE)}
                    />
                )}
                <CookiesDialog Link={Link} websiteURL={websiteURL} apiURL={apiURL} area={area} />
            </Fragment>
        </Paper>
    )
}

export default forwardRef(GlobalHeader)
